import { useState, useEffect } from 'react';
import { apiService } from '../services/api';

/**
 * Hook to manage edit history and version control
 * Handles fetching, restoring, and displaying version history
 */
export function useEditHistory(projectId: string | null) {
  const [editHistory, setEditHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showBranchedEdits, setShowBranchedEdits] = useState(true);

  const fetchEditHistory = async () => {
    if (!projectId) return;
    
    setLoadingHistory(true);
    try {
      const response = await apiService.get(`/editor/project/${projectId}/edits`);
      if (response.data.success && response.data.edits) {
        // Map backend fields to frontend interface
        const mappedHistory = response.data.edits.map((edit: any) => ({
          id: edit.id, // Use id (UUID) directly as the ID for restore
          summary: edit.summary || '[No description]',
          role: edit.source === 'ai_generation' ? 'assistant' : 'user',
          model: edit.ai_model || undefined,
          generated: edit.summary_generated || false,
          timestamp: edit.created_at,
          isActive: edit.is_current, // Track which edit is currently active
          onCurrentPath: edit.on_current_path, // Track if edit is on current path (for branching)
          isFutureEdit: edit.is_future_edit, // Track if edit is ahead of current position (future)
          // Database fields for publish modal
          version_number: edit.version_number,
          created_at: edit.created_at,
          updated_at: edit.updated_at,
          code_hash: edit.code_hash
        }));
        setEditHistory(mappedHistory);
        console.log('Loaded edit history:', mappedHistory.length, 'edits');
      }
    } catch (err) {
      console.error('Failed to fetch edit history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRestoreVersion = async (
    editId: string,
    onCodeUpdate: (code: string) => void,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    if (!projectId) return;
    
    setIsRestoring(true);
    try {
      console.log('📥 Restoring version:', editId);
      
      const response = await apiService.post(`/editor/project/${projectId}/restore/${editId}`);
      
      if (response.data.success && response.data.code) {
        // Update code in editor
        onCodeUpdate(response.data.code);
        console.log('✅ Restored version:', editId);
        
        // Refresh history to update active state
        await fetchEditHistory();
        onSuccess();
      } else {
        throw new Error(response.data.error || 'Failed to restore version');
      }
    } catch (err: any) {
      console.error('❌ Failed to restore version:', err);
      const errorMsg = err.response?.data?.error || 'Failed to restore edit';
      onError(errorMsg);
    } finally {
      setIsRestoring(false);
    }
  };

  // Don't auto-fetch on mount - let EditorPage call fetchEditHistory after project loads
  // This ensures auto-repair edits are created before fetching history

  return {
    editHistory,
    loadingHistory,
    isRestoring,
    showBranchedEdits,
    setShowBranchedEdits,
    fetchEditHistory,
    handleRestoreVersion
  };
}

